import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import ArrowLeft from '@/images/arrows/ArrowLeft.svg';
import { useCreateAvatarStore } from '@/pages/avatar/common/context';
import { useGetConsents } from '@/pages/avatar/hooks';
import { ScrollLeftButton, ScrollRightButton } from '@/pages/home/components/Common';
import { useAnalytics } from '@/services/analytics';
import { formatDate } from '@/utils/date';
import { MixPanelEvent } from '@/utils/types';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { ConsentDelete } from './ConsentDelete';

const ConsentSelectionContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;

    position: absolute;
    bottom: 0;
`;

const Title = styled.span`
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.4px;
`;

const ConsentsContainer = styled.div`
    display: flex;
    align-items: start;
    gap: 24px;
    max-width: calc(100vw - 96px);
    position: relative;
    max-height: 100px;
`;

const ScrollContainer = styled.div`
    display: flex;
    gap: 16px;
    outline: none;
    padding: 10px 10px 30px 10px;
    margin: -10px -10px -30px -10px;

    /* Hide scrollbar for Chrome, Safari and Opera */
    ::-webkit-scrollbar {
        display: none;
    }

    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    overflow-x: scroll;
`;

const ConsentCard = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 160px;
    height: 160px;
    cursor: pointer;
`;

const ImageContainer = styled.div`
    border-radius: 6px;
    position: relative;
    display: flex;
    align-items: center;

    overflow: hidden;
    width: 160px;
    height: 100px;
`;

const OverlayBackgroundImage = styled.div<{ bg: string }>`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;

    background-image: url(${props => props.bg});
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    filter: blur(8px);
    transform: scale(1.3);
`;

const ConsentImage = styled.img`
    z-index: 1;
    height: 100%;

    margin: 0 auto;
`;

const ConsentInfo = styled.div`
    display: flex;
    flex-direction: column;

    span {
        color: ${({ theme }) => theme.colors.secondary[900]};
        font-size: 14px;
        font-weight: 700;
        letter-spacing: -0.28px;

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    p {
        color: ${({ theme }) => theme.colors.black[700]};
        font-size: 12px;
        font-weight: 500;
        letter-spacing: -0.24px;
    }
`;

const StyledScrollRightButton = styled(ScrollRightButton)`
    right: -32px;
`;

const StyledScrollLeftButton = styled(ScrollLeftButton)`
    left: -32px;
`;

export const ConsentSelection = observer(() => {
    const createAvatarStore = useCreateAvatarStore();
    const { data } = useGetConsents();
    const mixpanel = useAnalytics();

    const ref = useRef<HTMLDivElement | null>(null);
    const [showRightScroll, setShowRightScroll] = useState<boolean>(false);
    const [showLeftScroll, setShowLeftScroll] = useState<boolean>(false);

    const calculateScroll = () => {
        const scrollWidth = ref?.current?.scrollWidth || 0;
        const offsetWidth = ref?.current?.offsetWidth || 0;
        const scrollLeft = ref?.current?.scrollLeft || 0;

        setShowRightScroll(scrollWidth > offsetWidth + scrollLeft);
        setShowLeftScroll(!!ref.current?.scrollLeft);
    };

    useEffect(() => {
        calculateScroll();
        window.addEventListener('resize', calculateScroll);
        ref.current?.addEventListener('scroll', calculateScroll);

        return () => {
            window.removeEventListener('resize', calculateScroll);
            ref.current?.removeEventListener('scroll', calculateScroll);
        };
    }, [data?.consents.length]);

    if (!data?.consents.length) {
        return null;
    }

    const handleScrollRight = () => {
        ref?.current?.scrollBy({ left: 1000, behavior: 'smooth' });
    };

    const handleScrollLeft = () => {
        ref?.current?.scrollBy({ left: -1000, behavior: 'smooth' });
    };

    return (
        <ConsentSelectionContainer>
            <Title>Use previous consent footage</Title>

            <ConsentsContainer>
                {showLeftScroll && (
                    <>
                        <StyledScrollLeftButton onClick={handleScrollLeft}>
                            <MaskedIcon src={ArrowLeft} size="24px" />
                        </StyledScrollLeftButton>
                    </>
                )}

                <ScrollContainer ref={ref}>
                    {data.consents.map(consent => (
                        <ConsentCard
                            key={consent.id}
                            onClick={() => {
                                mixpanel?.track('avatar-consent', {
                                    type: 'select',
                                    event: MixPanelEvent.CLICK,
                                });
                                createAvatarStore.setUsername(consent.name);
                                createAvatarStore.updateSelectedConsent(consent);
                            }}>
                            <ImageContainer>
                                <ConsentImage src={consent.thumbnail_url} />
                                <OverlayBackgroundImage bg={consent.thumbnail_url} />
                            </ImageContainer>

                            <ConsentDelete id={consent.id} />

                            <ConsentInfo>
                                <span>{consent.name}</span>
                                <p>{formatDate(consent.created_at)}</p>
                            </ConsentInfo>
                        </ConsentCard>
                    ))}
                </ScrollContainer>

                {showRightScroll && (
                    <>
                        <StyledScrollRightButton onClick={handleScrollRight}>
                            <MaskedIcon src={ArrowLeft} size="24px" rotated />
                        </StyledScrollRightButton>
                    </>
                )}
            </ConsentsContainer>
        </ConsentSelectionContainer>
    );
});
